import { useOutletContext, useNavigate, useParams, Link } from "react-router-dom";
import {
    ActivityCategoryInfo,
    ActivityOutletContext,
    BracketAdjustmentMode,
    MatchRoundConfig,
} from "../../interface";
import { useEffect, useRef, useState } from "react";
import { Button, Col, Form, Input, message, Popover, Row, Select, Typography, Space, Card, Modal } from "antd";
import {
    reloadMatchActivityCategory,
    updateMatchActivityCategory,
} from "../../apiBridge";
import {
    bracketStructureOptionsFor,
    buildCategoryRounds,
    categoryHasBronzeMatch,
    categoryHasExistingMatches,
    categoryRoundNamesForMode,
    categorySetupAdjustmentMode,
    configuredCategoryAdjustmentMode,
    maxParallelCourts,
    playableMatchCountsPerRound,
} from "./categorySetupUtils";

const { Text, Title } = Typography;

const COURT_COUNT_HINT =
    "You can keep all available courts. Choose fewer only when you want this category on one court and the others free for another category.";

const ActivityCategories: React.FC = () => {
    const [messageApi, contextHolder] = message.useMessage();
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { cid } = useParams();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pendingValues, setPendingValues] = useState<any>(null);
    const [confirmationMessage, setConfirmationMessage] = useState("");

    const { activity, categories, reloadData } = useOutletContext() as ActivityOutletContext;

    const [nowCategory, setNowCategory] = useState<ActivityCategoryInfo | null>(null);
    const [loadingState, setLoadingState] = useState(false);
    const [roundNames, setRoundNames] = useState<string[]>([]);
    const [adjustmentMode, setAdjustmentMode] =
        useState<BracketAdjustmentMode>("perfect");
    const roundConfigCache = useRef<Record<string, MatchRoundConfig>>({});
    const selectedStructure = Form.useWatch("bracket_structure", form) as string | undefined;

    const playableCounts = nowCategory
        ? playableMatchCountsPerRound(
            nowCategory.participant_count,
            adjustmentMode,
            categoryHasBronzeMatch(
                selectedStructure || nowCategory.options.bracket_structure,
            ),
        )
        : [];

    useEffect(() => {
        if (cid && categories) {
            const category = categories.find((cat) => String(cat.id) === String(cid));

            if (category) {
                const setupMode = categorySetupAdjustmentMode(category);
                const configuredRoundNames = categoryRoundNamesForMode(
                    category,
                    setupMode,
                );
                const counts = playableMatchCountsPerRound(
                    category.participant_count,
                    setupMode,
                    categoryHasBronzeMatch(category.options.bracket_structure),
                );
                const normalized = buildCategoryRounds(
                    configuredRoundNames,
                    category.round_names,
                    category.options.rounds,
                    {},
                    counts,
                    activity.max_court_count,
                );
                roundConfigCache.current = normalized.cache;
                setNowCategory(category);
                setAdjustmentMode(setupMode);
                setRoundNames(configuredRoundNames);
                form.setFieldsValue({
                    name: category.name,
                    bracket_structure: category.options.bracket_structure,
                    rounds: normalized.rounds,
                });
            } else {
                messageApi.error("Category not found");
                navigate(`/activity/${activity.id}/category`);
            }
        }
    }, [activity.id, activity.max_court_count, categories, cid, form, messageApi, navigate]);

    const reloadCategory = async () => {
        if (!nowCategory) {
            return false;
        }

        const res = await reloadMatchActivityCategory(activity.id, nowCategory.id);
        if (res) {
            const data = await res.json();

            if (data.error) {
                messageApi.error(data.error);
            } else {
                // eslint-disable-next-line no-restricted-globals
                location.reload();
            }
        }
    }

    const handleBracketStructureChange = (nextStructure: string) => {
        if (!nowCategory) {
            return;
        }
        const counts = playableMatchCountsPerRound(
            nowCategory.participant_count,
            adjustmentMode,
            categoryHasBronzeMatch(nextStructure),
        );
        const normalized = buildCategoryRounds(
            roundNames,
            roundNames,
            form.getFieldValue("rounds") || {},
            roundConfigCache.current,
            counts,
            activity.max_court_count,
        );
        roundConfigCache.current = normalized.cache;
        form.setFieldsValue({
            bracket_structure: nextStructure,
            rounds: normalized.rounds,
        });
    };

    const checkScheduleChanges = (values: any) => {
        if (!nowCategory) return false;

        const originalRounds = nowCategory.options.rounds;
        const newRounds = values.rounds;

        if (Object.keys(originalRounds).length !== Object.keys(newRounds).length) {
            return true;
        }
        
        for (const key in newRounds) {
            const roundKey = parseInt(key);
            const originalRound = originalRounds[roundKey];
            const newRound = newRounds[key];
            
            if (originalRound && (
                Number(originalRound.courts) !== Number(newRound.courts) ||
                Number(originalRound.game_rounds) !== Number(newRound.game_rounds) ||
                Number(originalRound.round_time) !== Number(newRound.round_time) ||
                Number(originalRound.rest_time) !== Number(newRound.rest_time)
            )) {
                return true;
            }
        }
        
        return false;
    }

    const onFinishUpdate = async (values: any) => {
        if (!nowCategory) {
            return false;
        }

        const hasScheduleChanges = checkScheduleChanges(values);
        const modeChanged =
            adjustmentMode !== configuredCategoryAdjustmentMode(nowCategory);
        const bracketStatus =
            nowCategory.bracket_status ||
            (nowCategory.is_bracket_done ? "published" : "not_created");
        
        if (modeChanged && bracketStatus === "draft") {
            setConfirmationMessage(
                "Changing the draw mode clears the bracket draft.",
            );
            setPendingValues(values);
            setIsModalOpen(true);
            return;
        }

        const bracketStructureChanged =
            values.bracket_structure !== nowCategory.options.bracket_structure;
        if (bracketStructureChanged && bracketStatus === "draft") {
            setConfirmationMessage(
                "Changing the bracket structure clears the bracket draft.",
            );
            setPendingValues(values);
            setIsModalOpen(true);
            return;
        }

        if (hasScheduleChanges && nowCategory.is_schedule_done) {
            setConfirmationMessage(
                "This change clears the current schedule. The bracket and existing match results are preserved.",
            );
            setPendingValues(values);
            setIsModalOpen(true);
            return;
        }

        await submitForm(values);
    }

    const submitForm = async (values: any) => {
        setLoadingState(true);

        let rounds: Map<number, MatchRoundConfig> = new Map();
        for (let key in values.rounds) {
            rounds.set(parseInt(key), {
                name: values.rounds[key].name,
                match_count: values.rounds[key].match_count,
                courts: values.rounds[key].courts,
                game_rounds: values.rounds[key].game_rounds,
                round_time: values.rounds[key].round_time,
                rest_time: values.rounds[key].rest_time,
            });
        }

        const res = await updateMatchActivityCategory(activity.id, {
            id: nowCategory!.id,
            name: values.name,
            bracket_adjustment_mode: adjustmentMode,
            round_names: roundNames,
            options: {
                bracket_structure: values.bracket_structure,
                rounds: Object.fromEntries(rounds),
            },
        });

        if (res) {
            const data = await res.json();

            if (data.error) {
                messageApi.error(data.error);
            } else if (data.discarded_seed_locks > 0) {
                messageApi.warning(
                    `Category updated. ${data.discarded_seed_locks} incompatible seed lock(s) were removed.`,
                );
                reloadData("category");
            } else {
                messageApi.success("updated");
                reloadData("category");
            }
        }

        setLoadingState(false);
    }

    const handleModalOk = () => {
        setIsModalOpen(false);
        if (pendingValues) {
            submitForm(pendingValues);
        }
        setConfirmationMessage("");
    }

    const handleModalCancel = () => {
        setIsModalOpen(false);
        setPendingValues(null);
        setConfirmationMessage("");
    }

    const renderRoundSetting = (
        roundName: string,
        roundIndex: number,
        isExtraMatchRound = false,
    ) => (
        <Col
            key={`${adjustmentMode}-${roundName}-${roundIndex}`}
            sm={8}
        >
            <Card style={{ marginBottom: 16 }}>
                <Space align="baseline" wrap>
                    <Title level={5}>{roundName}</Title>
                    {isExtraMatchRound && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Extra match round
                        </Text>
                    )}
                </Space>

                <Form.Item
                    name={["rounds", roundIndex, "courts"]}
                    label={
                        <Popover
                            content={COURT_COUNT_HINT}
                            trigger="hover"
                            styles={{ body: { maxWidth: 280 } }}
                        >
                            <span style={{ cursor: "help", borderBottom: "1px dashed rgba(0, 0, 0, 0.45)" }}>
                                Number of Courts
                            </span>
                        </Popover>
                    }
                >
                    <Select>
                        {Array.from(
                            { length: maxParallelCourts(playableCounts[roundIndex] ?? 1, activity.max_court_count) },
                            (_, index) => (
                                <Select.Option
                                    key={index + 1}
                                    value={index + 1}
                                >
                                    {index + 1}
                                </Select.Option>
                            ),
                        )}
                    </Select>
                </Form.Item>

                <Form.Item
                    name={["rounds", roundIndex, "game_rounds"]}
                    label="Round Format"
                >
                    <Select>
                        <Select.Option value={1}>Best of 1</Select.Option>
                        <Select.Option value={3}>Best of 3</Select.Option>
                        <Select.Option value={5}>Best of 5</Select.Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    name={["rounds", roundIndex, "round_time"]}
                    label="Round time"
                >
                    <Select disabled={true}>
                        <Select.Option value={60}>60s</Select.Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    name={["rounds", roundIndex, "rest_time"]}
                    label="Round Rest time"
                >
                    <Select disabled={true}>
                        <Select.Option value={60}>60s</Select.Option>
                    </Select>
                </Form.Item>
            </Card>
        </Col>
    );

    return (
        <>
            {contextHolder}
            <Modal
                title="Confirm Category Setup Change"
                open={isModalOpen}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
            >
                <p>{confirmationMessage}</p>
                <p>Do you want to continue?</p>
            </Modal>
            <Space direction="vertical" style={{ width: '100%' }}>
                <Card style={{ marginBottom: 20 }}>
                    <Title level={5}>{nowCategory?.name}</Title>

                    <Space direction="vertical" style={{ width: '100%' }}>
                        {nowCategory && !nowCategory.need_reload ? (
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Text strong>Participant Count: {nowCategory.participant_count}</Text>

                                <Form
                                    form={form}
                                    name="updateCategoryForm"
                                    layout="vertical"
                                    onFinish={onFinishUpdate}
                                >
                                    <Form.Item style={{ marginTop: 5 }}>
                                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                            <Button type="primary" htmlType="submit" loading={loadingState}>
                                                Save
                                            </Button>

                                            <Link to={`/activity/${activity.id}/category`}>Back List</Link>
                                        </Space>
                                    </Form.Item>

                                    <Card style={{ background: "#fafafa" }}>
                                        <Form.Item label="Category Name" name="name">
                                            <Input />
                                        </Form.Item>

                                        <Form.Item
                                            label="Bracket Structure"
                                            name="bracket_structure"
                                            extra={
                                                categoryHasExistingMatches(nowCategory)
                                                    ? "Matches already exist. Bracket structure cannot be changed."
                                                    : undefined
                                            }
                                        >
                                            <Select
                                                options={bracketStructureOptionsFor(activity.match_type)}
                                                onChange={handleBracketStructureChange}
                                                disabled={categoryHasExistingMatches(nowCategory)}
                                            />
                                        </Form.Item>

                                        <Title level={4}>Round Settings</Title>
                                        <Row gutter={24}>
                                            {roundNames.map((roundName, roundIndex) =>
                                                renderRoundSetting(
                                                    roundName,
                                                    roundIndex,
                                                    adjustmentMode === "extra" &&
                                                        roundIndex === 0,
                                                ),
                                            )}
                                        </Row>
                                    </Card>
                                </Form>
                            </Space>
                        ) : (nowCategory && nowCategory?.need_reload ? (
                            <Space direction="vertical">
                                <Text>You may have modified the participant information or category settings. Please click "reload" to reset the relevant category information.</Text>
                                <Button type="primary" onClick={() => reloadCategory()}>
                                    Reload Category
                                </Button>
                            </Space>
                        ) : (
                            <Text>No category data</Text>
                        ))}
                    </Space>
                </Card>
            </Space>
        </>
    );
}

export default ActivityCategories;
